import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOptionalAuth } from '../state/AuthContext';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function UserMenu() {
  const auth = useOptionalAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!auth) {
    return null;
  }

  const { user, organisations, loading, logout } = auth;

  if (loading) {
    return <div className="user-menu user-menu--loading" aria-hidden="true" />;
  }

  if (!user) {
    return (
      <div className="user-menu" data-testid="user-menu-guest">
        <Link to="/login" className="btn ghost compact">
          Sign in
        </Link>
      </div>
    );
  }

  const label = user.profile?.displayName || user.fullName || user.email;
  const orgLabel = organisations[0]?.name;

  async function onLogout() {
    setOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="user-menu" ref={rootRef} data-testid="user-menu">
      <button
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-avatar" aria-hidden="true">
          {user.profile?.avatarMediaAssetId ? (
            <img src="/api/auth/avatar" alt="" />
          ) : (
            <span>{initials(label)}</span>
          )}
        </span>
        <span className="user-menu__meta">
          <span className="user-menu__name">{label}</span>
          {orgLabel ? <span className="user-menu__org">{orgLabel}</span> : null}
        </span>
      </button>
      {open ? (
        <div className="user-menu__dropdown" role="menu" id={menuId}>
          <Link role="menuitem" to="/settings/profile" onClick={() => setOpen(false)}>
            Profile
          </Link>
          <Link role="menuitem" to="/settings/account" onClick={() => setOpen(false)}>
            Account
          </Link>
          <Link role="menuitem" to="/settings/organisation" onClick={() => setOpen(false)}>
            Organisation
          </Link>
          <Link role="menuitem" to="/settings/security" onClick={() => setOpen(false)}>
            Security
          </Link>
          <button type="button" role="menuitem" onClick={() => void onLogout()}>
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
